interface RulerProps {
  orientation: 'horizontal' | 'vertical'
  lengthMm: number
  pxPerMm: number
  zoom: number
}

export function Ruler({ orientation, lengthMm, pxPerMm, zoom }: RulerProps) {
  const step = zoom >= 2 ? 1 : zoom >= 1 ? 5 : 10
  const ticks: Array<{ mm: number; major: boolean }> = []
  for (let mm = 0; mm <= lengthMm; mm += step) {
    ticks.push({ mm, major: mm % (step * 5) === 0 })
  }

  const isH = orientation === 'horizontal'
  const sizePx = lengthMm * pxPerMm * zoom
  // A tick exactly at the label extent (offset === sizePx) would sit outside
  // the clipped box — pull it 1px in so the final tick stays visible.
  const tickOffset = (mm: number) => Math.min(mm * pxPerMm * zoom, sizePx - 1)

  return (
    <div
      aria-hidden="true"
      className="relative overflow-hidden bg-stone-100 text-[8px] text-stone-500 dark:bg-stone-900 dark:text-stone-400"
      style={
        isH
          ? { height: 20, width: sizePx }
          : { width: 20, height: sizePx }
      }
    >
      {ticks.map(({ mm, major }) => {
        const offset = tickOffset(mm)
        const style: Record<string, string | number> = isH
          ? { left: offset }
          : { top: offset }
        return (
          <div
            key={mm}
            data-testid="ruler-tick"
            data-mm={mm}
            className="absolute"
            style={{
              ...style,
              ...(isH
                ? { top: major ? 2 : 8, bottom: 0, width: 1 }
                : { left: major ? 2 : 8, right: 0, height: 1 }),
              background: 'currentColor',
            }}
          />
        )
      })}
      {ticks
        .filter(t => t.major)
        // Drop a label whose text would start at/beyond the label extent —
        // it would either be clipped to nothing or (unclipped) overshoot.
        .filter(({ mm }) => mm * pxPerMm * zoom + 2 < sizePx)
        .map(({ mm }) => {
          const offset = mm * pxPerMm * zoom
          return (
            <span
              key={`label-${mm}`}
              className="absolute"
              style={
                isH
                  ? { left: offset + 2, top: -1 }
                  : { top: offset + 2, left: 1 }
              }
            >
              {mm}
            </span>
          )
        })}
    </div>
  )
}
